// src/pages/api/lkpEntityAPI.ts
//
// Lookup ENTITY registry + harmonization API. Aligns 1:1 with the router routes:
//   GET  /entities/{eid}
//   GET  /entities/{eid}/{leid}
//   POST /entities/{eid}/rebuild?fold_authoritative=
//   POST /entities/{eid}/fold-authoritative
//   POST /entities/{eid}/action
//   POST /entities/{eid}/harmonize/propose | auto | apply | agent
//
// Uses the same apiGet/apiPost as lkpAPI.ts.

import { apiGet, apiPost } from "../API";

const _BASE_LKP = "/adriva_api/lookup";

/* ── entity model types ─────────────────────────────────────────────────────── */
export type EntityStatus = "discovered" | "sourced" | "harmonized" | "published";
export type ValueStatus = "active" | "deprecated";
export type ValueProvenance = "client" | "profiling_promoted" | "sme_manual";

export interface Binding {
  field_id: string; table: string; column: string;
  role: string; source: string; confidence?: number | null;
  label_field?: { table: string; column: string } | null;
  business_name?: string | null;
  review?: { status: string; confirmed: boolean };
}
export interface SourceValue {
  code: string; display?: string | null; seen_in: string[];
  origin: string; count?: number | null;
}
export interface CanonicalValue {
  code: string; display?: string | null; status: ValueStatus; provenance: ValueProvenance;
  // data-governance fields (added by the reference upload)
  governance_status?: string;      // Approved / Obsolete / Draft / Candidate / Submitted For Approval / Not Documented Yet
  description?: string;          // value-level business meaning
  promoted_to_ekd?: boolean;     // whether promoted into the EKD whitelist
  changed_by?: string; changed_at?: string;
  history?: Array<{ at: string; by: string; action: string; from?: any; to?: any }>;
}
export interface CrosswalkEntry { source_code: string; canonical_code: string; method?: string; }

export interface LookupEntity {
  entity_id: string; name: string; description?: string | null;
  status: EntityStatus; steward?: string | null;
  bindings: Binding[]; source_values: SourceValue[];
  canonical_values: CanonicalValue[]; crosswalk: CrosswalkEntry[];
  flags?: Record<string, any>; review?: Record<string, any>;
  buckets?: BucketCounts;                 // present in list responses
}
export interface BucketCounts {
  mapped: number; unmapped_source: number; unused_canonical: number;
  canonical: number; source: number;
}
export interface Buckets {
  mapped: Array<{ source_code: string; display?: string; canonical_code: string; origin?: string; seen_in?: string[]; implicit?: boolean }>;
  unmapped_source: Array<{ source_code: string; display?: string; origin?: string; seen_in?: string[] }>;
  unused_canonical: CanonicalValue[];
  counts: BucketCounts;
}
export interface EntityRelationship {
  type: string; determinant_field: string; dependent_field: string;
  value_map: Record<string, string[]>; stats?: any; status: string;
}
export interface AuthoritativeProposal {
  field_id: string; bound_entity_id: string; bound_entity_name: string;
  client_logical_field: string; score?: number; runner_up?: any; action: string;
}
export interface EntitySummary {
  entities: number; by_status: Record<string, number>;
  bound_fields: number; unmapped_source_values: number;
}
export interface EntitiesResponse {
  success: boolean; entity_id: string;
  entities: LookupEntity[]; relationships: EntityRelationship[];
  summary: EntitySummary; authoritative_proposals: AuthoritativeProposal[];
}

export type EntityActionName =
  | "create" | "rename" | "set_status" | "set_steward" | "accept" | "reject"
  | "bind" | "unbind" | "merge" | "split"
  | "add_canonical" | "set_canonical_status" | "set_all_canonical_status" | "map_crosswalk" | "promote_source"
  | "auto_seed" | "publish" | "promote_all_source";

export interface EntityActionResponse {
  success: boolean; entity: LookupEntity | null; buckets: Buckets | null;
  entities: LookupEntity[]; summary: EntitySummary;
}

/* ── entity registry calls ──────────────────────────────────────────────────── */
export async function lookup_getEntities(entityId: string): Promise<EntitiesResponse> {
  return apiGet(`${_BASE_LKP}/entities/${encodeURIComponent(entityId)}`);
}

export async function lookup_getEntity(
  entityId: string, leid: string,
): Promise<{ success: boolean; entity: LookupEntity; buckets: Buckets }> {
  return apiGet(`${_BASE_LKP}/entities/${encodeURIComponent(entityId)}/${encodeURIComponent(leid)}`);
}

/** Step 1: (re)build entities from discovery candidates; optionally fold client values. */
export async function lookup_rebuildEntities(
  entityId: string, foldAuthoritative = false,
): Promise<{ success: boolean; entities: LookupEntity[]; summary: EntitySummary; authoritative_proposals: AuthoritativeProposal[] }> {
  return apiPost(`${_BASE_LKP}/entities/${encodeURIComponent(entityId)}/rebuild?fold_authoritative=${foldAuthoritative}`, {});
}

/** Step 2: attach client authoritative values to entities as canonical evidence. */
export async function lookup_foldAuthoritative(
  entityId: string,
): Promise<{ success: boolean; attached: number; proposals: AuthoritativeProposal[]; entities: LookupEntity[] }> {
  return apiPost(`${_BASE_LKP}/entities/${encodeURIComponent(entityId)}/fold-authoritative`, {});
}

/** All SME mutations go through the action dispatcher. */
export async function lookup_entityAction(
  entityId: string, action: EntityActionName,
  opts: { entity_id?: string; payload?: Record<string, any>; reviewedBy?: string } = {},
): Promise<EntityActionResponse> {
  return apiPost(`${_BASE_LKP}/entities/${encodeURIComponent(entityId)}/action`, {
    action, entity_id: opts.entity_id, payload: opts.payload || {}, reviewed_by: opts.reviewedBy || "SME",
  });
}

/* ── entity-name harmonization (standalone, on-demand) ──────────────────────── */
export interface HarmonizeCluster {
  canonical_entity_id: string; canonical_name: string;
  member_entity_ids: string[];
  members?: Array<{ entity_id: string; name: string; bindings: number }>;
  method?: string; confidence?: number;
}
export interface HarmonizeProposal {
  auto: HarmonizeCluster[]; review: HarmonizeCluster[]; singletons: string[];
}

/** Non-mutating: deterministic clusters (auto = safe, review = needs a look). */
export async function lookup_harmonizePropose(
  entityId: string,
): Promise<{ success: boolean } & HarmonizeProposal> {
  return apiPost(`${_BASE_LKP}/entities/${encodeURIComponent(entityId)}/harmonize/propose`, {});
}

/** Apply only the high-confidence (core_match) clusters; return the rest for review. */
export async function lookup_harmonizeAuto(
  entityId: string,
): Promise<{ success: boolean; applied: number; review: HarmonizeCluster[]; entities: LookupEntity[] }> {
  return apiPost(`${_BASE_LKP}/entities/${encodeURIComponent(entityId)}/harmonize/auto`, {});
}

/** Apply an SME-approved (or agent-proposed) set of clusters. */
export async function lookup_harmonizeApply(
  entityId: string, clusters: HarmonizeCluster[],
): Promise<{ success: boolean; entities_merged: number; entities: LookupEntity[] }> {
  return apiPost(`${_BASE_LKP}/entities/${encodeURIComponent(entityId)}/harmonize/apply`, { clusters });
}

/** Optional semantic pass: ask the agent to cluster by concept. Returns PROPOSED
 *  clusters (not auto-applied). appNm is optional metadata for the agent call. */
export async function lookup_harmonizeAgent(
  entityId: string, appNm?: string,
): Promise<{ success: boolean; proposed: HarmonizeCluster[]; keep_separate: Array<{ name: string; reason: string }> }> {
  return apiPost(`${_BASE_LKP}/entities/${encodeURIComponent(entityId)}/harmonize/agent`,
    appNm ? { app_nm: appNm } : {});
}
